import React, { useMemo, useState } from 'react';
import StatusSelection from './components/StatusSelection';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import EmailTagSearchInput from '../../../components/EmailTagSearchInput';
import { useQuery, useMutation } from 'react-query';
import { adminAPI } from '../../../api';
import useDebounce from '../../../hooks/useDebounce';
import useAlarm from '../../../context/AlarmContext';
import FormLoader from '../../../components/FormLoader';
import AddRepresentativeSidebar from './components/add-representative-sidebar';
import SidebarUserEdit from './components/sidebar/UserEdit';
import SideActionBoard from '../../../components/SideActionBoard';
import IndependentUserInviteForm from '../../../components/admin/users/IndependentUserInviteForm';
import RemoveModalRepresentative from './components/DeleteModalRepresentative';
import useModal from '../../../context/ModalContext';
import EyeIcon from '../../../../../../assets/images/reskin-images/icon--eye.svg';
import EditIcon from '../../../../../../assets/images/reskin-images/icon--edit.svg';
import ChevronRightIcon from '../../../../../../assets/images/reskin-images/icon--chevrons-right.svg';
import SVG from 'react-inlinesvg';
import { Link } from 'react-router-dom';

const RepresentativesTab = ({ cohort, id, refetch }) => {
  if (!cohort) return <div>Loading...</div>;

  const [currentPage, setCurrentPage] = useState(1);
  const [checkedRepresentatives, setCheckedRepresentatives] = useState([]);
  const [checkedAllRepresentatives, setCheckedAllRepresentatives] = useState(false);
  const [emailTags, setEmailTags] = useState([]);
  const [rowHovered, setRowHovered] = useState(false);
  const { setContent } = useModal();
  const [filter, setFilter] = useState({role: 'Representative'});
  const [status, setStatus] = useState('All');
  const [isExporting, setIsExporting] = useState(false);
  const [openSideActionBoard, setOpenSideActionBoard] = useState(false);
  const [openSideActionBoardUserEdit, setOpenSideActionBoardUserEdit] = useState(false);
  const { setAlarm } = useAlarm();

  const handleSelectAllRepresentatives = (selectAll = false) => {
    if (selectAll) {
      setCheckedAllRepresentatives(true);
    } else {
      setCheckedAllRepresentatives(false);
      setCheckedRepresentatives([]);
    }
  };

  const handleEmailTagsChange = (tags) => {
    setEmailTags(tags);
    setCheckedAllRepresentatives(false);
    setCheckedRepresentatives([]);
  };

  const debouncedEmailTags = useDebounce(emailTags, 300);
  const debouncedFilter = useDebounce(filter, 300);
  const debouncedCurrentPage = useDebounce(currentPage, 300);

  const apiFilter = useMemo(() => {
    const f = { ...debouncedFilter };
    if (debouncedEmailTags.length > 0) {
      f.emails = [...debouncedEmailTags];
    }
    return f;
  }, [debouncedFilter, debouncedEmailTags]);

  const { data: representativesData, refetch: refetchRepresentatives } = useQuery(
    ['getCohortUsers', { id, page: debouncedCurrentPage, filter: apiFilter, search: '' }],
    adminAPI.getCohortUsers,
    {
      enabled: !!id,
    }
  );
  
  const { mutate: removeRepresentativesMutation, isLoading: isRemoving } = useMutation(
    adminAPI.removeRepresentativesFromCohort,
    {
      onSuccess: () => {
        setAlarm({ type: 'success', message: `Representative${checkedRepresentatives.length !== 1 ? 's' : ''} removed successfully` });
        refetchRepresentatives();
        setCheckedRepresentatives([]);
      },
      onError: (error) => {
        setAlarm({ type: 'error', message: error.message });
      },
    }
  );

  const handleRemoveRepresentatives = () => {
    const userEmails = checkedRepresentatives.map((p) => p.email);
    removeRepresentativesMutation({ id, cohort: { userEmails } });
  }

  const columns = [
    {
      label: 'Name',
      field: 'name',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '260px',
    },
    {
      label: 'Email',
      field: 'email',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '35%',
    },
    {
      label: 'User Access Type',
      field: 'user_access_type',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Alex Access Type',
      field: 'alex_access_type',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Cohorts',
      field: 'cohorts',
      textAlign: 'left',
      fontWeight: 'semibold',
      width: '15%',
    },
    {
      label: 'Alex Expiration Date',
      field: 'alex_expiration_date',
      textAlign: 'center',
      width: '10%',
    },
    {
      label: 'User Expiration Date',
      field: 'user_expiration_date',
      textAlign: 'center',
      width: '10%',
    },
    {
      label: 'Status',
      field: 'status',
      textAlign: 'center',
      width: '10%',
    },
  ];

  const formatDate = (dateString) => {
    if (!dateString) {
      return '-';
    }
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    };
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', options);
    return formattedDate.replace(/\//g, '-');
  };

  const getStatusButton = (status) => {
    if (status === 'Active') {
      return (
        <div className="flex items-center justify-center bg-green-lightest rounded-lg px-2">
          Active
        </div>
      );
    } else if (status === 'Confirmed') {
      return (
        <div className="flex items-center justify-center bg-grey-lightest rounded-lg px-2">
          Completed
        </div>
      );
    } else if (status === 'Pending' || status === 'Unconfirmed') {
      return (
        <div className="flex items-center justify-center bg-yellow-lightest rounded-lg px-2">
          {status}
        </div>
      );
    }
    return status;
  };
  
  const rowFormatter = (representative) => {
    const actionButtons = (
      <div
        className={`flex h-full items-center ${
          rowHovered === representative.id ? 'visible' : 'invisible'
        }`}
      >
        <button
          type="button"
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg mr-2 hover:bg-purple-200"
          onClick={() => {
            setOpenSideActionBoardUserEdit({
              type: 'user-view',
              user_id: representative.user_id
            });
          }}
        >
          <SVG src={EyeIcon} />
        </button>     
        <Link
          className="flex items-center justify-content-center bg-purple-100 p-2 rounded-lg hover:bg-purple-200"
          onClick={() =>
            setOpenSideActionBoardUserEdit({
              type: 'user-edit',
              user_id: representative.user_id
            })
          }
        >
          <SVG src={EditIcon} />
        </Link>
      </div>
    );

    return {
      ...representative,
      id: representative.id,
      user_id: representative.user_id,
      name: (
        <div className="flex flex-no-wrap whitespace-no-wrap items-center justify-between">
          {representative.name ? representative.name : '-'}
          {representative.user_id && actionButtons}
        </div>
      ),
      email: representative.email,
      user_access_type: representative.user_access_type || '-',
      alex_access_type: representative.alex_access_type || '-',
      cohorts: representative.cohorts || '-',
      alex_expiration_date: formatDate(representative.alex_expiration_date),
      user_expiration_date: formatDate(representative.user_expiration_date),
      status: getStatusButton(representative.status),
    }
  };

  const handleStatusChange = (status) => {
    setCheckedAllRepresentatives(false);
    setCheckedRepresentatives([]);
    setCurrentPage(1);
    setStatus(status);
    const newFilter = { ...filter };
    if (status === 'All') {
      delete newFilter.status;
    } else {
      newFilter.status = status;
    }
    setFilter(newFilter);
  };

  const validRepresentativesCount = representativesData?.users?.filter(user => user.user_id != null).length || 0;
  const totalValidRepresentativesCount = representativesData?.pagination?.total_count_with_user_id || representativesData?.pagination?.total_count || 0;

  const selectedValidRepresentatives = checkedRepresentatives
    .map(checkedItem => {
      const fullRepresentative = representativesData?.users?.find(u => u.id === checkedItem.id);
      return fullRepresentative && fullRepresentative.user_id ? {
        id: fullRepresentative.user_id,
        email: fullRepresentative.email
      } : null;
    })
    .filter(p => p !== null);
  const selectedValidCount = selectedValidRepresentatives.length;

  const handleExportCohortUsers = () => {
    const timestamp = new Date().getTime() / 1000;
    setIsExporting(true);

    const params = new URLSearchParams();
    params.append('format', 'csv');
    params.append('t', timestamp);
    
    const exportFilter = { ...filter };
    if (emailTags.length > 0) {
      exportFilter.emails = [...emailTags];
    }
    if (Object.keys(exportFilter).length > 0) {
      params.append('filter', JSON.stringify(exportFilter));
    }

    const baseUrl = `/api/v2/admin/cohorts/${id}/get_cohort_users?${params.toString()}`;

    fetch(baseUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((response) => {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/csv')) {
          return response.blob().then(blob => ({ isFile: true, data: blob }));
        } else if (contentType && contentType.includes('application/json')) {
          return response.json().then(json => ({ isFile: false, data: json }));
        } else {
          return response.json().then(json => ({ isFile: false, data: json }));
        }
      })
      .then((result) => {
        if (result.isFile) {
          const urlLink = window.URL.createObjectURL(result.data);
          const link = document.createElement('a');
          link.href = urlLink;
          link.setAttribute('download', `cohort_representatives_${cohort.name}_${timestamp}.csv`);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(urlLink);
          
          setAlarm({ type: 'success', message: 'Export completed successfully!' });
        } else {
          setAlarm({ 
            type: 'success', 
            message: result.data.message || 'Export processing started. You will receive notifications about the progress.' 
          });
        }
        setIsExporting(false);
      })
      .catch((error) => {
        console.error(error);
        setAlarm({ type: 'error', message: 'Export failed. Please try again.' });
        setIsExporting(false);
      });
  };

  return (
    <>
      {openSideActionBoard && (
        <AddRepresentativeSidebar openSideActionBoard={openSideActionBoard} setOpenSideActionBoard={setOpenSideActionBoard} onSuccess={refetchRepresentatives}/>
      )}
      {openSideActionBoardUserEdit && openSideActionBoardUserEdit.type === 'user-view' && (
        <SidebarUserEdit
          openSideActionBoard={openSideActionBoardUserEdit}
          setOpenSideActionBoard={setOpenSideActionBoardUserEdit}
          user_id={openSideActionBoardUserEdit.user_id}
          role="Representative"
        />
      )}
      {openSideActionBoardUserEdit && openSideActionBoardUserEdit.type === 'user-edit' && (
        <SidebarUserEdit
          openSideActionBoard={openSideActionBoardUserEdit}
          setOpenSideActionBoard={setOpenSideActionBoardUserEdit}
          onSuccess={refetchRepresentatives}
          cohorts={openSideActionBoardUserEdit.cohorts}
          user_id={openSideActionBoardUserEdit.user_id}
        />
      )}
      {openSideActionBoardUserEdit && openSideActionBoardUserEdit.type === 'user-edit-bulk' && (
        <SidebarUserEdit
          openSideActionBoard={openSideActionBoardUserEdit}
          setOpenSideActionBoard={setOpenSideActionBoardUserEdit}
          onSuccess={refetchRepresentatives}
          ids={openSideActionBoardUserEdit.ids}
          checkedItems={checkedRepresentatives}
          checkedAllUsers={openSideActionBoardUserEdit.checkedAllUsers}
          totalUsers={openSideActionBoardUserEdit.totalUsers}
          cohorts={openSideActionBoardUserEdit.cohorts}
          filter={openSideActionBoardUserEdit.filter}
          searchQuery={openSideActionBoardUserEdit.searchQuery || ''}
        />
      )}
      {openSideActionBoardUserEdit && openSideActionBoardUserEdit.type === 'user-create-invites-bulk' && (
        <>
        <SideActionBoard setOpenSideActionBoard={setOpenSideActionBoardUserEdit}>
          <div className="flex flex-no-wrap whitespace-no-space justify-between items-center">
            <button
              className="p-2 flex items-center -ml-2"
              type="button"
              onClick={() => setOpenSideActionBoardUserEdit()}
            >
              <SVG src={ChevronRightIcon} />
            </button>
            <span
              style={{ width: 'calc(100% + 6rem)' }}
              className="-mx-16 h-px bg-gray flex"
            />
          </div>
          <IndependentUserInviteForm
            title="Invite Users"
            setOpenSideActionBoard={setOpenSideActionBoardUserEdit}
          />
        </SideActionBoard>
        </>
      )}

      <div className="py-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <EmailTagSearchInput
              cohortId={id}
              suggestionFilter={debouncedFilter}
              emailTags={emailTags}
              onEmailTagsChange={handleEmailTagsChange}
              active
              className="w-full"
            />
          </div>
          <div className="flex-none">
            <button
              type="button"
              className="px-4 py-2 font-semibold bg-purple-100 hover:bg-purple-200 rounded-lg"
              onClick={handleExportCohortUsers}
              disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
        {isExporting && <FormLoader />}

        <StatusSelection
          status={status}
          handleStatusChange={handleStatusChange}
        />

        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4"> 
            <h3 className="text-lg font-semibold">
              Representative{representativesData?.pagination?.total_count !== 1 ? 's' : ''} ({representativesData?.pagination?.total_count > 0 ? representativesData?.pagination?.total_count : 0})
            </h3>
            {(checkedRepresentatives.length > 0 || checkedAllRepresentatives) && (
              <button
                type="button"
                className="px-4 py-2 font-semibold text-red transition-colors"
                onClick={
                  () => {
                    setContent({
                      content: <RemoveModalRepresentative ids={checkedRepresentatives} cohortId={id} checkedAllRepresentatives={checkedAllRepresentatives} refetch={refetchRepresentatives} refetchRepresentatives={refetchRepresentatives} setCheckedRepresentatives={setCheckedRepresentatives} />,
                    });
                  }
                }
              >
                {checkedRepresentatives.length > 0 && !checkedAllRepresentatives && `Remove Selected (${checkedRepresentatives.length})`}
                {checkedAllRepresentatives && `Remove Selected (${representativesData?.pagination?.total_count > 0 ? representativesData?.pagination?.total_count : 0})`}
              </button>
            )}

            {(checkedRepresentatives.length > 0 || checkedAllRepresentatives) && (
              <button
                type="button"
                className="flex items-center font-semibold bg-purple-100 rounded-lg py-3 px-4 hover:bg-purple-lightest transition-colors mr-4"
                onClick={() => {
                  setOpenSideActionBoardUserEdit({
                    type: 'user-edit-bulk',
                    ids: selectedValidRepresentatives,
                    checkedAllUsers: checkedAllRepresentatives,
                    totalUsers: totalValidRepresentativesCount,
                    filter: {
                      ...filter,
                      only_with_user_id: true
                    },
                    searchQuery: emailTags.join(','),
                  });
                }}
              >
                <SVG src={EditIcon} className="mr-2" />
                Edit {checkedAllRepresentatives ? totalValidRepresentativesCount.toLocaleString('en-US') : checkedRepresentatives.length} User{checkedRepresentatives.length >= 2 || checkedAllRepresentatives ? 's' : ''}
              </button>
            )}
          </div>
          <div>
            {/* <button
              type="button"
              className="px-4 py-2 font-semibold bg-purple-100 hover:bg-purple-200 rounded-lg mr-4"
              onClick={() =>
                setOpenSideActionBoardUserEdit({
                  type: 'user-create-invites-bulk',
                  ids: [],
                  emails: [],
                })
              }
              >
                Invite Users (need to refactor this component)
            </button> */}            
              <button
                  type="button"
                  className="px-4 py-2 font-semibold bg-purple-100 hover:bg-purple-200 rounded-lg mr-4"
                  onClick={() =>
                      setOpenSideActionBoard({
                          type: 'add-representative',
                          id: id,
                      })
                  }
                  >
                  Add Representatives
              </button>
          </div>
        </div>

        <div style={{overflow: 'auto'}}>
          <Table
            id="representatives"
            nameOfRecord="Representative"
            data={representativesData?.users}
            columns={columns}
            reconstructorFn={rowFormatter}
            setRowHovered={setRowHovered}
            checkedItems={checkedRepresentatives}
            setCheckedItems={setCheckedRepresentatives}
            totalCount={representativesData?.pagination?.total_count}
            onSelectAllRecords={handleSelectAllRepresentatives}
            checkedAllUsers={checkedAllRepresentatives}
          />
          {<Pagination
            currentPage={currentPage}
            totalPage={representativesData?.pagination?.total_pages}
            onPageChange={(page) => setCurrentPage(page)}
          />}
        </div>

      </div>
    </>
  );
};

export default RepresentativesTab;
